import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import service from "../../auth/FetchInterceptor";

// ÉTAT INITIAL GÉNÉRIQUE
const initialState = {
	data: [], // Liste d'éléments (read)
	currentItem: null, // Élément en cours d'édition ou de visualisation
	loading: false, // Indicateur général du chargement
	error: null, // Gestion des erreurs
	createData: null, // Données pour la création
};

// ACTION ASYNCHRONE : CREATE
export const createItem = createAsyncThunk("crud/createItem", async ({ url, item }, { rejectWithValue }) => {
	try {
		const response = await service({
			method: "POST",
			url,
			data: item,
		});
		return response.data; // Retourne l'élément créé
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || "Erreur lors de la création");
	}
});

// ACTION ASYNCHRONE : READ (Fetch de tous les éléments)
export const fetchItems = createAsyncThunk("crud/fetchItems", async (url, { rejectWithValue }) => {
	try {
		const response = await service({
			method: "GET",
			url,
		});
		return response.data; // Retourne les données
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || "Erreur lors du chargement des données");
	}
});

// ACTION ASYNCHRONE : UPDATE
export const updateItem = createAsyncThunk("crud/updateItem", async ({ url, item }, { rejectWithValue }) => {
	try {
		const response = await service({
			method: "PUT",
			url,
			data: item,
		});
		return response.data; // Retourne l'élément mis à jour
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || "Erreur lors de la mise à jour");
	}
});

// ACTION ASYNCHRONE : DELETE
export const deleteItem = createAsyncThunk("crud/deleteItem", async ({ url }, { rejectWithValue }) => {
	try {
		await service({
			method: "DELETE",
			url,
		});
		return url; // Retourne l'identifiant ou URL supprimé
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || "Erreur lors de la suppression");
	}
});

export const createDataSlice = createAsyncThunk("crud/createData", async (url, { rejectWithValue }) => {
	try {
		const response = await service({
			method: "GET",
			url,
		});
		return response.data; // Retourne les données
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || "Erreur lors du chargement des données");
	}
});

// SLICE CRUD GÉNÉRAL
export const crudSlice = createSlice({
	name: "crud",
	initialState,
	reducers: {
		setCurrentItem: (state, action) => {
			state.currentItem = action.payload; // Définit l'élément courant
		},
		clearError: (state) => {
			state.error = null; // Efface les erreurs
		},
	},
	extraReducers: (builder) => {
		// CREATE
		builder
			.addCase(createItem.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createItem.fulfilled, (state, action) => {
				state.loading = false;
				state.data.push(action.payload); // Ajoute l'élément créé
			})
			.addCase(createItem.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		// READ
		builder
			.addCase(fetchItems.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchItems.fulfilled, (state, action) => {
				state.loading = false;
				state.data = action.payload; // Charge les données dans l'état
			})
			.addCase(fetchItems.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		// UPDATE
		builder
			.addCase(updateItem.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateItem.fulfilled, (state, action) => {
				state.loading = false;
				// Met à jour l'élément modifié dans la liste
				const index = state.data.findIndex((item) => item.id === action.payload.id);
				if (index !== -1) {
					state.data[index] = action.payload;
				}
			})
			.addCase(updateItem.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		// DELETE
		builder
			.addCase(deleteItem.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteItem.fulfilled, (state, action) => {
				state.loading = false;
				// Supprime l'élément de la liste
				state.data = state.data.filter((item) => item.url !== action.payload);
			})
			.addCase(deleteItem.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(createDataSlice.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createDataSlice.fulfilled, (state, action) => {
				state.loading = false;
				state.createData = action.payload;
			})
			.addCase(createDataSlice.rejected, (state, action) => {
				state.loading = false;
				state.error = action;
			});
	},
});

export const { setCurrentItem, clearError } = crudSlice.actions;

export default crudSlice.reducer;
